import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { Box, Modal, TextField } from "@mui/material";
import Loader from "../../components/Loader";
import axios from "../../MyAxios";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@emotion/react";
import clsx from "clsx";
import {
  GET_COMMENTS,
  POST,
  POST_COMMENT,
  POST_DELETE,
  ROOT,
  UPLOAD_FILE,
} from "../../constants/api";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Layout from "../../Layout";
import send from "../../assets/img/send.svg";
import Post from "../../components/Post";
import Comment from "../../components/Comment";
import useLoggedIn from "../../hooks/useLoggedIn";
import ShowMore from "@components/ShowMore";
import CommentInput from "@components/CommentInput/CommentInput";
import { setPosts, updatePost } from "@redux/prevPostsSlice";
import { getLinkMetadata } from "../../redux/postSlice";

// ── Extract first URL from any text string ────────────────────────────────────
const extractUrl = (text) => {
  if (!text) return null;
  let t = String(text).trim();
  try {
    t = decodeURIComponent(t);
  } catch (e) { }
  const match =
    t.match(/https?:\/\/[^\s<>'")\]]+/i) || t.match(/www\.[^\s<>'")\]]+/i);
  if (!match) return null;
  let link = match[0].replace(/[.,;:!?')\]]+$/g, "");
  if (!/^https?:\/\//i.test(link)) link = "https://" + link;
  try {
    return new URL(link).toString();
  } catch (e) {
    return null;
  }
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 350,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
};

export default function ViewPost() {
  // useLoggedIn();
  const theme = useTheme();
  const [expanded, setExpanded] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [comments, setComments] = React.useState([]);
  const [commentsLoading, setCommentsLoading] = React.useState(false);
  const [commentToReply, setCommentToReply] = React.useState(null);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [comment, setComment] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const navigation = useNavigate();
  const { id } = useParams();
  const [post, setPost] = React.useState();
  const { userRecord } = useSelector((state) => state.user);
  const [file, setFile] = React.useState("");
  const [prevFile, setPrevFile] = React.useState();
  const [commentUploading, setCommentUploading] = React.useState(false);
  const [commentToEdit, setCommentToEdit] = React.useState(null);
  const [commentToOverwrite, setCommentToOverwrite] = React.useState(null);

  // ── OG state for comment input ─────────────────────────────────────────────
  const [commentOgData, setCommentOgData] = React.useState(null);
  const [commentOgLoading, setCommentOgLoading] = React.useState(false);
  const [commentOgRemoved, setCommentOgRemoved] = React.useState(false);

  const [voteStatus, setVoteStatus] = React.useState({});

  const dispatch = useDispatch();
  const prevPosts = useSelector((state) => state.prevPosts);
  const location = useLocation();
  const handleExpandClick = () => setExpanded(!expanded);

  // ── OG helpers ─────────────────────────────────────────────────────────────
  // ── Decode HTML entities from OG metadata (&#x27; → ', &amp; → &, etc.) ────
  const decodeHtmlEntities = (str) => {
    if (!str || typeof str !== "string") return str;
    try {
      const txt = document.createElement("textarea");
      txt.innerHTML = str;
      return txt.value;
    } catch (e) {
      return str;
    }
  };
  const decodeMetadata = (metadata) => {
    if (!metadata || typeof metadata !== "object") return metadata;
    const decoded = {};
    for (const [key, val] of Object.entries(metadata)) {
      decoded[key] = typeof val === "string" ? decodeHtmlEntities(val) : val;
    }
    return decoded;
  };

  const handleCommentOgFetched = (link) => {
    setCommentOgLoading(true);
    dispatch(
      getLinkMetadata({
        link,
        onSuccess: (metadata) => {
          const decoded = decodeMetadata(metadata);
          setCommentOgData({ sourceLink: link, ...decoded });
          setCommentOgRemoved(false);
          // If current comment is just the URL (or empty), replace with OG description + link
          try {
            const cur = (comment || "").trim();
            const isJustUrl =
              cur === "" || cur === link || cur === link.replace(/\/$/, "");
            if (decoded?.ogDescription && isJustUrl) {
              setComment(`${decoded.ogDescription}\n${link}`);
            }
          } catch (e) { }
        },
        onError: () => setCommentOgData(null),
      }),
    ).finally(() => setCommentOgLoading(false));
  };

  const handleDismissCommentOg = () => {
    setCommentOgData(null);
    setCommentOgRemoved(true);
  };

  const resetCommentOg = () => {
    setCommentOgData(null);
    setCommentOgLoading(false);
  };

  const options = [
    { id: 1, name: "Open" },
    { id: 2, name: "Connect" },
    { id: 3, name: "Copy Link" },
    { id: 4, name: "Save" },
    { id: 5, name: "Report" },
  ];

  const onComment = async (e) => {
    e.preventDefault();
    if (!userRecord?.id) return;
    if (!comment && !file) return;

    const formData = new FormData();

    let mediaId = "";
    if (commentToEdit?.media?.currBucketUrl) {
      mediaId = commentToEdit?.media?.id;
    } else if (commentToOverwrite?.media?.currBucketUrl) {
      mediaId = commentToOverwrite?.media?.id;
    }

    if (file) {
      setCommentUploading(true);
      formData.append("file", file);
      await axios
        .post(UPLOAD_FILE, formData)
        .then((res) => {
          if (res.data) {
            mediaId = res.data;
          } else {
            toast.error("Something went wrong while uploading media");
          }
        })
        .catch(() => { })
        .finally(() => {
          setPrevFile(null);
          setCommentUploading(false);
        });
    }

    let newPost = prevPosts.posts.filter(
      (prevPost) => prevPost.id === post.id,
    )[0];
    if (!newPost && prevPosts.posts) {
      const rawId = String(post.id).replace(/^event-/, "");
      newPost = prevPosts.posts.filter((prevPost) => prevPost.id === rawId)[0];
    }
    if (newPost) {
      newPost = { ...newPost, comments: (newPost.comments || 0) + 1 };
    }

    // EDIT
    if (commentToEdit) {
      try {
        const payload = { comment, mediaId: prevFile ? mediaId : null };
        if (commentOgRemoved) {
          payload.postLinkMetadata = null;
          payload.hideLinkPreview = true;
        } else if (commentOgData) {
          payload.postLinkMetadata = commentOgData;
          payload.hideLinkPreview = false;
        }
        await axios.post(`/edit/comment/${commentToEdit.id}`, payload);
        setComments([]);
        getComments();
      } catch (e) {
        // ignore
      } finally {
        setCommentToEdit(null);
        setComment("");
        resetCommentOg();
        setCommentOgRemoved(false);
      }

      // OVERWRITE
    } else if (commentToOverwrite) {
      try {
        const payload = {
          postId: post.id,
          commentId: commentToOverwrite.id,
          newComment: comment,
          mediaId: prevFile ? mediaId : null,
        };
        if (commentOgRemoved) {
          payload.postLinkMetadata = null;
          payload.hideLinkPreview = true;
        } else if (commentOgData) {
          payload.postLinkMetadata = commentOgData;
          payload.hideLinkPreview = false;
        }
        await axios.post(`/group-admin-comment-overwrite`, payload);
        setComments([]);
        setPrevFile(null);
        getComments();
      } catch (e) {
        // ignore
      } finally {
        setCommentToOverwrite(null);
        setComment("");
        setPrevFile(null);
        resetCommentOg();
        setCommentOgRemoved(false);
      }

      // REPLY
    } else if (commentToReply) {
      const replyTo = commentToReply?.id;
      try {
        setCommentUploading(true);
        const payload = {
          userId: userRecord.id,
          postId: post.id,
          comment,
          replyTo,
          mediaId,
          postLinkMetadata: commentOgData || null,
          hideLinkPreview: !commentOgData,
        };
        const res = await axios.post(POST_COMMENT, payload);
        await axios.post("/notification/create/reply", {
          commentId: commentToReply?.id,
        });
        const newComments = [...comments];
        const index = newComments.findIndex((c) => c.id === replyTo);
        if (!newComments[index].replies) newComments[index].replies = [];
        const replyWithOg = {
          ...res.data,
          postLinkMetadata: commentOgData || res.data.postLinkMetadata || null,
          hideLinkPreview: !commentOgData,
        };
        newComments[index].replies = [
          replyWithOg,
          ...newComments[index].replies,
        ];
        setComments(newComments);
        setComment("");
        resetCommentOg();
        if (newPost) dispatch(updatePost(newPost));
      } catch (error) {
        // ignore
      } finally {
        setCommentUploading(false);
        setCommentToReply(null);
      }

      // NORMAL
    } else {
      try {
        setCommentUploading(true);
        const payload = {
          userId: userRecord.id,
          postId: post.id,
          comment,
          mediaId,
          postLinkMetadata: commentOgData || null,
          hideLinkPreview: !commentOgData,
        };
        const res = await axios.post(POST_COMMENT, payload);
        const postIdForApi =
          typeof post.id === "string" && post.id.startsWith("event-")
            ? post.id
            : post?.isEvent
              ? `event-${post.id}`
              : post.id;

        await axios.post("/notification/create/comment", {
          postId: postIdForApi,
        });
        const commentWithOg = {
          ...res.data,
          postLinkMetadata: commentOgData || res.data.postLinkMetadata || null,
          hideLinkPreview: !commentOgData,
        };
        setComments([commentWithOg, ...comments]);
        setComment("");
        resetCommentOg();
        if (newPost) dispatch(updatePost(newPost));
      } catch (error) {
        // ignore
      } finally {
        setCommentToReply(null);
        setCommentUploading(false);
      }
    }
  };

  const isPostGroupCommunityDisabled = (post) => {
    let isDisabled = true;
    post?.groups.forEach((group) => {
      if (group.community?.disabledAt === 0) isDisabled = false;
    });
    return isDisabled;
  };

  const isPostCommunityDisabled = (post) => {
    let isDisabled = true;
    post?.communities.forEach((com) => {
      if (com?.disabledAt === 0) isDisabled = false;
    });
    return isDisabled;
  };

  const isReadOnly = () => {
    return (
      !userRecord?.id ||
      (Boolean(post?.groups?.length) && isPostGroupCommunityDisabled(post)) ||
      (Boolean(post?.communities?.length) && isPostCommunityDisabled(post)) ||
      (Boolean(post?.groups?.length) && post?.groups?.[0]?.disabledAt > 0)
    );
  };

  const getCommentRestrictionError = () => {
    if (isReadOnly()) {
      return userRecord?.id? "Activity restricted due to disabled group or community" : "Log-in to interact";
    }
    return false;
  };

  const getPost = async () => {
    try {
      const res = await axios.get(`${POST}/${id}`);
      setPost(res.data);
    } catch (error) {
      navigation(-1);
    }
  };

  React.useEffect(() => {
    getPost();
    return () => {
      delete localStorage.postClickTrigger;
    };
  }, []);

  React.useEffect(() => {
    if (!post?.id) return;
    axios
      .get(`/post/vote-status/${post.id}`)
      .then((res) => {
        setVoteStatus(res.data)
      })
      .catch((err) => console.error("Failed to fetch vote status:", err));
  }, [post]);

  // ── Re-fetch OG for comments that came back from API without postLinkMetadata ─

  const getComments = async () => {
    try {
      setCommentsLoading(true);
      const res = await axios.get(`${GET_COMMENTS}/${post?.id}/${page}`);
      const loaded = res.data.comments || [];
      setComments((comments) => [...comments, ...loaded]);
      setTotalPages(res.data.totalPages);
    } catch (e) {
      // ignore
    } finally {
      setCommentsLoading(false);
    }
  };

  React.useEffect(() => {
    if (post?.id) {
      try {
        getComments();
      } catch (e) {
        toast.error(`Error: occurred while fetching comments`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, post?.id]);

  if (!post) return <Loader />;

  const onCommentDelete = (id) => {
    if (!userRecord?.id) return;
    if (
      window.confirm("Are you sure you want to delete this comment?") === false
    )
      return;
    axios
      .post(`/delete/comment/${id}`)
      .then((res) => {
        toast.success("Comment deleted successfully");
        setComments([]);
        getComments();
        let newPost = prevPosts.posts.filter(
          (prevPost) => prevPost.id === post.id,
        )[0];
        newPost = { ...newPost, comments: newPost.comments - 1 };
        dispatch(updatePost(newPost));
      })
      .catch((err) => { });
  };

  const handleDeleteCommunityPost = async (postId) => {
    if (!userRecord?.id) return;

    if (
      window.confirm(
        "Are you sure you want to delete this community post permanently?",
      )
    )
      onDelete(postId);
  };

  const onDelete = async (postId) => {
    if(!userRecord?.id) return;
    try {
      toast.info("Deleting Post");
      await axios.post(POST_DELETE, { postId }).then((res) => {
        toast.success("Post Deleted Successfully");
        navigation(ROOT);
      });
    } catch (error) {
      console.error(error);
    }
  };

  const isGroupPost = post?.groups?.length >= 1;
  const userIsPostGroupAdmin =
    isGroupPost && post.groups.some((group) => group.admin === userRecord.id);
  const postCommunity = post?.communities?.[0];
  const userCanRemoveCommunityPost =
    postCommunity?.adminAccessLevels?.canDeleteCommunityPost &&
    post.groups?.length === 0 &&
    postCommunity?.admin === userRecord?.id;

  return (
    <Layout>
      <div
        style={{ maxHeight: "80vh" }}
        className="flex flex-col justify-between"
      >
        <div className="z-50 w-full bg-white">
          <Post
            readOnly={isReadOnly()}
            fullTitle
            fullDescription
            post={post}
            comment={false}
            back={true}
            setPost={setPost}
            canRemoveCommunityPost={userCanRemoveCommunityPost}
            handleRemoveCommunityPost={handleDeleteCommunityPost}
            returnTo={location.pathname} // ✅ Post will forward this as state.returnTo when navigating to edit
            voteStatus={voteStatus}
          />
        </div>
        <div
          style={{ borderColor: theme.palette.primary.main }}
          className="px-3 flex border-t-2 flex-col justify-between"
        >
          <div
            style={{
              maxHeight: post?.media?.currBucketUrl ? "40vh" : "60vh",
              overflowY: "scroll",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            className="flex flex-col pb-20 pt-3"
          >
            {commentsLoading ? (
              <div className="flex justify-center py-6">
                <div className="loader" />
              </div>
            ) : (
              comments?.map((comment) => (
                <Comment
                  readOnly={isReadOnly()}
                  key={comment?.id}
                  comment={comment}
                  post={post}
                  setCommentToReply={setCommentToReply}
                  setComment={setComment}
                  onDelete={onCommentDelete}
                  setCommentToEdit={(c) => {
                    setCommentToEdit(c);
                    // Seed OG state so the existing preview is preserved on save
                    const meta = c?.postLinkMetadata;
                    if (meta) {
                      const parsed =
                        typeof meta === "string"
                          ? (() => {
                            try {
                              return JSON.parse(meta);
                            } catch (e) {
                              return null;
                            }
                          })()
                          : meta;
                      setCommentOgData(parsed || null);
                    } else {
                      setCommentOgData(null);
                    }
                    // Ensure we mark that the OG was not removed by the user when editing
                    setCommentOgRemoved(false);
                  }}
                  setPrevFile={setPrevFile}
                  overwritable={
                    userIsPostGroupAdmin &&
                    comment.user.id !== userRecord.id &&
                    comment.overwrittenBy?.id !== userRecord.id
                  }
                  overwriteLevel="group"
                  setCommentToOverwrite={setCommentToOverwrite}
                />
              ))
            )}
            <div
              onClick={() => setPage(page + 1)}
              className={clsx(
                totalPages <= page && "hidden",
                "grow cursor-pointer my-2 text-center mx-4 text-sm font-normal",
              )}
            >
              <ShowMore>Show More</ShowMore>
            </div>
          </div>
        </div>

        {getCommentRestrictionError() ? (
          <Typography className="py-3 px-2 text-center fixed bottom-0 w-full md:w-6/12 bg-white">
            {getCommentRestrictionError()}
          </Typography>
        ) : (
          <CommentInput
            value={comment}
            setValue={setComment}
            onSubmit={onComment}
            setFile={setFile}
            prevFile={prevFile}
            setPrevFile={setPrevFile}
            loading={commentUploading}
            ogData={commentOgData}
            ogLoading={commentOgLoading}
            onOgFetched={handleCommentOgFetched}
            onDismissOg={handleDismissCommentOg}
          />
        )}

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            {options.map((option) => (
              <Link
                to={option.name}
                key={option.id}
                className="block py-2 text-sm"
              >
                {option.name}
              </Link>
            ))}
            {post?.user?.id === userRecord?.id && (
              <Typography
                onClick={() => onDelete(post)}
                className="block py-2 text-sm"
              >
                Delete
              </Typography>
            )}
          </Box>
        </Modal>
      </div>
    </Layout>
  );
}
